import { Injector } from '@angular/core';
import { Art57Section } from './Art57Section/Art57Section';
import { FormlyFieldConfig } from '@ngx-formly/core';

export class BulkUtils {
  static cleanModel<T>(input: T, keysToRemove: string[]): T {
    if (input === null || typeof input !== 'object') {
      return input;
    }

    // Detect and fix malformed array-like objects
    const isArrayLikeObject =
      Array.isArray(input) && Object.keys(input).some((k) => isNaN(Number(k)));

    if (Array.isArray(input) && !isArrayLikeObject) {
      return input.map((item) => this.cleanModel(item, keysToRemove)) as any;
    }

    const keys = Object.keys(input);
    const out: Record<string, any> = {};

    for (const key of keys) {
      if (keysToRemove.includes(key)) {
        continue;
      }

      const val = (input as any)[key];
      out[key] = this.cleanModel(val, keysToRemove);
    }

    return out as T;
  }

  static transformRepeaterFields(
    jsonOrObj: string | any,
    groupKeys: string[],
  ): any {
    const model =
      typeof jsonOrObj === 'string' ? JSON.parse(jsonOrObj) : jsonOrObj;
    const result: any = Array.isArray(model) ? [...model] : { ...model };

    function collectSelectedRecursive(elem: any, acc: any[]) {
      if (elem == null) return;

      const isArrayLikeObject =
        Array.isArray(elem) && Object.keys(elem).some((k) => !isNaN(Number(k)));

      if (Array.isArray(elem) || isArrayLikeObject) {
        for (const it of elem) collectSelectedRecursive(it, acc);
        return;
      }

      if (typeof elem === 'object') {
        let sel = elem.selected;

        if ((!Array.isArray(sel) || sel.length === 0) && typeof elem.term === 'string') {
          const termValues = elem.term
            .split('|')
            .map((v: string) => v.trim())
            .filter((v: string) => v.length > 0);

          if (termValues.length > 0) {
            // opcional: reflejar en el modelo
            elem.selected = termValues;
            sel = termValues;
          }
        }

        if (Array.isArray(sel)) {
          acc.push(...sel);
        }

        return;
      }
    }
    
    for (const groupKey of groupKeys) {
      const group = model[groupKey];
      if (!group || typeof group !== 'object') {
        result[groupKey] = null;
        continue;
      }

      const isActive = group.filterStatus === true;
      if (!isActive) {
        result[groupKey] = null;
        continue;
      }

      const filterData = group.filterData;
      const acc: any[] = [];
      collectSelectedRecursive(filterData, acc);
      result[groupKey] = acc.length == 0 ? null : Array.from(new Set(acc));
    }

    return result;
  }

  static transformNonRepeaterFields(
    jsonOrObj: string | any,
    fieldsToProcess: string[],
  ): any {
    const model =
      typeof jsonOrObj === 'string' ? JSON.parse(jsonOrObj) : jsonOrObj;
    const result: any = Array.isArray(model) ? [...model] : { ...model };

    // Recursively collect all arrays from nested structures
    function collectArrays(obj: any, acc: any[][]) {
      if (obj == null) return;

      if (Array.isArray(obj)) {
        acc.push(obj);
        for (const item of obj) {
          collectArrays(item, acc);
        }
      } else if (typeof obj === 'object') {
        for (const key of Object.keys(obj)) {
          collectArrays(obj[key], acc);
        }
      }
    }

    for (const field of fieldsToProcess) {
      const fieldData = model[field];

      // If empty continue
      if (fieldData == null) continue;

      // if filterStatus is not active return null for this field
      if (!fieldData.filterStatus) {
        result[field] = null;
        continue;
      }

      const acc: any[][] = [];
      collectArrays(fieldData, acc);

      result[field] = acc.length == 0 ? null : acc.flat();
    }

    return result;
  }

  static transformOtherFields(
    jsonOrObj: string | any,
    fieldsToProcess: string[],
  ): any {
    const result = { ...jsonOrObj };

    for (const key of fieldsToProcess) {
      const section = jsonOrObj[key];

      if (section && typeof section === 'object' && 'filterData' in section) {
        if (section.filterStatus) {
          result[key] = section.filterData;
        } else {
          result[key] = null;
        }
      }
    }

    return result;
  }

  static collectActiveFieldValue(model: any, fieldKeys: string[]): number {
    let result: number = 0;

    function traverse(obj: any) {
      if (!obj || typeof obj !== 'object') return;

      if (fieldKeys.length == 1) {
        if (fieldKeys[0] in obj && obj[fieldKeys[0]] === true) {
          result = result + 1;
        }
      } else {
        let isTrue = true;
        fieldKeys.forEach((fieldKey) => {
          isTrue = isTrue && fieldKey in obj && obj[fieldKey] === true;
        });
        if (isTrue) {
          result = result + 1;
        }
      }

      for (const key of Object.keys(obj)) {
        const value = obj[key];

        if (Array.isArray(value)) {
          for (const item of value) {
            traverse(item);
          }
        } else if (typeof value === 'object') {
          traverse(value);
        }
      }

      if (obj.fieldGroup) {
        traverse(obj.fieldGroup);
      }

      if (obj.fieldArray) {
        traverse(obj.fieldArray);
      }

      if (obj.formGroup) {
        traverse(obj.formGroup);
      }
    }

    traverse(model);
    return result;
  }

  static bulkModelTransform(injector: Injector, model: any): any {
    const criteriaCleaned = this.cleanModel(model, [
      'hasSelectedData',
      'datasource',
// to allow | copy&paste in bulkseltypes:      'term',
      'lastOrder',
      'componentID',
    ]);


    const repeatersTransformed = this.transformRepeaterFields(
      criteriaCleaned,
      injector.get(Art57Section).getSelectedSection().repeatersToTransform,
    );
    const nonRepeatersTransformed = this.transformNonRepeaterFields(
      repeatersTransformed,
      injector.get(Art57Section).getSelectedSection().nonRepeatersToTransform,
    );
    const otherFieldsTransformed = this.transformOtherFields(
      nonRepeatersTransformed,
      injector.get(Art57Section).getSelectedSection().otherFieldsToTransform,
    );
    
    return otherFieldsTransformed;
  }

  static getSelectedCheckboxLabels(fields: FormlyFieldConfig[], model: any): string {
  const labels: string[] = [];

  const traverse = (fieldList: FormlyFieldConfig[]) => {
    for (const field of fieldList) {
      if (!field || typeof field !== 'object') continue;

      if (field.fieldGroup && Array.isArray(field.fieldGroup)) {
        const fg = field.fieldGroup;

        const checkbox = fg[0];
        const dataField = fg[2]; 

        if (
          checkbox &&
          checkbox.type === 'checkbox' &&
          checkbox.key === 'filterStatus'
        ) {
          const parentKey = field.key;
          const filterStatusValue = model?.[parentKey as string]?.filterStatus;

          if (filterStatusValue === true && dataField?.props?.label) {
            labels.push(dataField.props.label);
          }
        }

        traverse(field.fieldGroup);

        // fieldArray.repeaters
        if (field.fieldArray && 'fieldGroup' in field.fieldArray && Array.isArray(field.fieldArray.fieldGroup)) {
          traverse(field.fieldArray.fieldGroup);
        }

        continue;
      }

      if (field.fieldGroup) {
        traverse(field.fieldGroup);
      }

        if (
          field.fieldArray &&
          'fieldGroup' in field.fieldArray &&
          Array.isArray(field.fieldArray.fieldGroup)
        ) {
          traverse(field.fieldArray.fieldGroup);
        } 
    }
  };

  traverse(fields);

  return labels.join(' / ');
}

}
